/**
 * Overview dashboard: totals, split NFC/QR, 30d chart, top collaboratori.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
} from "recharts";

import api, { extractErr } from "../api";
import { Card, PageHeader, StatCard, EmptyState } from "../ui";

export default function Dashboard() {
    const [overview, setOverview] = useState(null);
    const [staff, setStaff] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        Promise.all([api.get("/stats/overview"), api.get("/stats/staff?preset=30d")])
            .then(([o, s]) => {
                setOverview(o.data);
                setStaff(s.data);
            })
            .catch((e) => setErr(extractErr(e)));
    }, []);

    if (err) return <div className="text-red-600 text-sm">{err}</div>;
    if (!overview) return <div className="text-slate-500 text-sm">Caricamento…</div>;

    const chartData = (overview.daily_30d || []).map((d) => ({
        day: d.date.slice(5),
        Accessi: d.count,
    }));

    return (
        <div data-testid="dashboard-page">
            <PageHeader
                title="Panoramica"
                description="Accessi al link di recensione Google generati tramite tag NFC e QR code."
            />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <StatCard label="Totali" value={overview.total} testId="stat-total" />
                <StatCard label="Oggi" value={overview.today} testId="stat-today" />
                <StatCard label="Ultimi 7 gg" value={overview.last_7d} testId="stat-7d" />
                <StatCard label="Ultimi 30 gg" value={overview.last_30d} testId="stat-30d" />
                <StatCard label="Via NFC" value={overview.nfc} testId="stat-nfc" />
                <StatCard label="Via QR" value={overview.qr} testId="stat-qr" />
                <StatCard label="Origine sconosciuta" value={overview.total - overview.nfc - overview.qr} testId="stat-unknown" />
                <StatCard label="Collaboratori attivi" value={(staff?.rows || []).filter((r) => r.active).length} testId="stat-active-staff" />
            </div>

            <Card className="p-5 mb-6">
                <div className="flex items-baseline justify-between mb-4">
                    <div>
                        <div className="text-[10px] tracking-[0.24em] uppercase text-slate-500">
                            Andamento
                        </div>
                        <div className="text-lg font-semibold">Ultimi 30 giorni</div>
                    </div>
                </div>
                <div style={{ width: "100%", height: 260 }}>
                    <ResponsiveContainer>
                        <AreaChart data={chartData} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                            <defs>
                                <linearGradient id="acc" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#0f172a" stopOpacity={0.28} />
                                    <stop offset="100%" stopColor="#0f172a" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                            <XAxis dataKey="day" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                            <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                            <Tooltip
                                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
                            />
                            <Area type="monotone" dataKey="Accessi" stroke="#0f172a" strokeWidth={2} fill="url(#acc)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </Card>

            <Card className="overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200 flex items-baseline justify-between">
                    <div>
                        <div className="text-[10px] tracking-[0.24em] uppercase text-slate-500">
                            Interazioni per collaboratore
                        </div>
                        <div className="text-lg font-semibold">Ultimi 30 giorni</div>
                    </div>
                    <Link
                        to="/admin/collaboratori"
                        className="text-xs text-slate-600 hover:text-slate-900"
                    >
                        Vedi tutti →
                    </Link>
                </div>
                {(staff?.rows || []).length === 0 ? (
                    <EmptyState
                        title="Nessun collaboratore registrato"
                        description="Aggiungi un collaboratore per iniziare a tracciare le interazioni per persona."
                    />
                ) : (
                    <table className="w-full text-sm">
                        <thead className="text-[11px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="text-left px-5 py-3">Collaboratore</th>
                                <th className="text-left px-5 py-3">Tag attivi</th>
                                <th className="text-right px-5 py-3">Oggi</th>
                                <th className="text-right px-5 py-3">7 gg</th>
                                <th className="text-right px-5 py-3">30 gg</th>
                                <th className="text-right px-5 py-3">Media/gg</th>
                            </tr>
                        </thead>
                        <tbody>
                            {staff.rows.slice(0, 10).map((r) => (
                                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                                    <td className="px-5 py-3">
                                        <Link
                                            to={`/admin/collaboratori/${r.id}`}
                                            className="font-medium hover:underline"
                                            data-testid={`dashboard-staff-row-${r.id}`}
                                        >
                                            {r.first_name} {r.last_name}
                                        </Link>
                                        {!r.active && (
                                            <span className="ml-2 text-[10px] text-slate-400">
                                                disattivato
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-5 py-3 text-slate-600">
                                        {r.current_tags.length === 0
                                            ? "—"
                                            : r.current_tags.join(", ")}
                                    </td>
                                    <td className="px-5 py-3 text-right tabular-nums">{r.today}</td>
                                    <td className="px-5 py-3 text-right tabular-nums">{r.last_7d}</td>
                                    <td className="px-5 py-3 text-right tabular-nums font-semibold">{r.window_total}</td>
                                    <td className="px-5 py-3 text-right tabular-nums text-slate-500">
                                        {r.window_avg_per_day.toFixed(1)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            <p className="text-[11px] text-slate-400 mt-6 max-w-2xl">
                Le metriche misurano gli <em>accessi</em> generati verso Google, non le
                recensioni effettivamente pubblicate. Google non condivide automaticamente
                l'esito della recensione.
            </p>
        </div>
    );
}
